import * as Constants from "./constants";
import createBallAndStick from "./models/ball-and-stick";
import createSpaceFilling from "./models/space-filling";
import createStick from "./models/stick";
import createLine from "./models/line";
import parseSDF from "./sdf-parser";
import RenderManager from "../rendering/render-manager";
import SlideManager from "../utils/slide-manager";
import ObjectManager from "../objects/object-manager";
import MolecularMesh from "./molecular-mesh";

const regexpFormulaName = /^[\w\-\(\\)\ \,]+$/i;
const regexpFormulaCid = /^cid:(?<cid>\d+)$/i;

class Molecule extends THREE.Group {
    constructor(formula, type = "ball-stick") {
        super();

        this._formula = formula;
        this._displayType = type;
        this.type = "Molecule";
        this.isMolecule = true;
    }

    get regexpFormulaName() { return regexpFormulaName; }

    _disposeOld() {
        this.children.forEach(x => {
            x.traverse(object => {
                if (!object.isMoleculeMesh) return;

                const { uuid } = object;

                const materials = object.material instanceof Array ? object.material : [object.material];

                materials.forEach(material => material.dispose());
                object.geometry.dispose();

                // Q3.data.assets.objects[this.uuid].meshes = [];
                delete Q3.data.assets.objects[uuid];
                delete Q3.assets.objects[uuid];
                SlideManager.slides.forEach(({ animation }) => delete animation[uuid]);

                RenderManager.raycaster.untrack(object);

            });
        });

        while (this.children.pop()) { }
    }

    _setDisplayType(type) {
        this._displayType = type;
        this.setFormula(this._formula, true);
    }

    async createMeshes(assetUri) {
        this._disposeOld();
        let moleculeData;
        try {
            moleculeData = await (assetUri
                    ? downloadMoleculeAsset(assetUri)
                    : retrieveMoleculeData(this._formula)
            );
        } catch (err) {
            console.log(err);
        }
        if(!moleculeData) return null;

        const { molecules, uri: _assetUri } = moleculeData;
        const colorScheme = Constants.ColorScheme.ChimeraCPK;
        const radii = Constants.CovalentRadii;

        const createModel = getCreationModel(this._displayType);

        createModel(molecules, colorScheme, radii)
            .forEach(({ geometry, material }) => {
                this.add(new MolecularMesh(geometry, material));
            });

        Q3.RenderManager.activeCamera.update = true;

        return assetUri ? assetUri : _assetUri;
    }

    async setFormula(query, force = false) {
        if (this._formula === query && !force)
            return null;

        this._formula = query;
        this._disposeOld();

        const { molecules, uri: _assetUri } = await retrieveMoleculeData(query);

        const colorScheme = Constants.ColorScheme.ChimeraCPK;
        const radii = Constants.CovalentRadii;

        const createModel = getCreationModel(this._displayType);

        const data = Q3.data.assets.objects[this.uuid];
        const moleculeList = data.meshes = [];

        createModel(molecules, colorScheme, radii)
            .forEach(params => {
                const { object } = ObjectManager
                    .addObject("molecular-mesh", THREE.Math.generateUUID(), this.uuid, data, params);

                moleculeList.push(object.uuid);

                this.add(object);
            });

        Q3.RenderManager.activeCamera.update = true;

        return _assetUri;
    }

    get formula() { return this._formula; }

    get displayType() { return this._displayType; }
    set displayType(v) { this._setDisplayType(v); }
}

export default Molecule;
export { Molecule };

async function downloadMoleculeAsset(uri) {
    uri = uri.replace(/^https:\/\/(?:coobo|cooboprod)\.blob\.core\.windows\.net\/(?:(?:web|coobo)\/)?assets/, "");
    const response = await fetch(Q3.s3Url+uri);
    const data = await response.text();
    const molecules = parseSDF(data);

    return { molecules, uri };
}

async function retrieveMoleculeData(query) {
    let api, formula, match;

    if (match = regexpFormulaName.exec(query)) {
        api = "name";
        formula = query;
    } else if (match = regexpFormulaCid.exec(query)) {
        api = "cid";
        formula = match.groups.cid;
    } else throw new Error(`Invalid query type: ${query}`);

    const response = await fetch(`${(window.COOBO ? "/api" : "")}/get_molecule/${Q3.data.s3Id}/${api}/${formula}`);

    if (Q3.editMode && response.status === 404) {
        Q3.showSnackBar(`PubChem does not provide 3D structure for "${query}".`);
    }

    const { data, fileid } = await response.json();
    const molecules = parseSDF(data);
    const uri = `/${Q3.data.s3Id}/${fileid}.cid`;

    return { molecules, uri };
}

function getCreationModel(displayType) {
    switch (displayType) {
        case "line": return createLine;
        case "stick": return createStick;
        case "filling": return createSpaceFilling;
        case "ball-stick":
        default:
            return createBallAndStick;
    }
}